import { Fragment, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import {
  Alert,
  Button,
  Card, CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody, ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import { AccordionElement } from 'src/component/Accordion';
import ProductionCycleChargesTable from 'src/component/ProductionCycleChargesTable/ProductionCycleChargesTable';
import ProductionCycleProductsTable from 'src/component/ProductionCycleProductsTable/ProductionCycleProductsTable';
import ProductivityTable from 'src/component/ProductivityTable/ProductivityTable';
import SweetAlert from 'sweetalert2';

import { useNatureCharges } from 'src/hooks/useNatureCharges';
import { useNatureProducts } from 'src/hooks/useNatureProducts';
import { usePractices } from 'src/hooks/usePractices';
import { useProductionCycle } from 'src/hooks/useProductionCycle';

import { IProductionCycle, ProductionModes } from 'src/pages/accueil/model/types';
import { useHttpClient } from 'src/security/hooks/axiosProvider';
import { parseProductionCyclePath } from 'src/utils/utilMethods';
import Breadcrumb from '../../../breadcrumb';

const ProductionCycleEdit = (props: any) => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const httpClient = useHttpClient(true);

  const { productionCycle, loading: loadingCycle } = useProductionCycle();
  const { practices: allPractices } = usePractices();
  const projectId = productionCycle?.tpe?.project?.id;
  const activityId = productionCycle?.subActivity?.activity?.id;
  const { natureCharges } = useNatureCharges(projectId, activityId);
  const { natureProducts } = useNatureProducts(projectId, activityId);

  const [relatedCycles, setRelatedCycles] = useState<IProductionCycle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [spinner, setSpinner] = useState(false);

  const [modalPractices, setModalPractices] = useState(false);
  const [modalFinancial, setModalFinancial] = useState(false);

  const [editItem, setEditItem] = useState<any>(null);
  const [editType, setEditType] = useState<'CHARGE' | 'PRODUCT'>('CHARGE');
  const [financialAlert, setFinancialAlert] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const [formData, setFormData] = useState<any>({
    label: '',
    startDate: '',
    endDate: '',
    previousCrop: '',
    mode: ProductionModes.conventionnel,
    dimension: 0,
    pratiques: [],
    productivites: [],
    charges: [],
    produits: [],
  });

  useEffect(() => {
    if (productionCycle) {
      setFormData({
        ...productionCycle,
        startDate: productionCycle.startDate ? new Date(productionCycle.startDate).toISOString().split('T')[0] : '',
        endDate: productionCycle.endDate ? new Date(productionCycle.endDate).toISOString().split('T')[0] : '',
        pratiques: productionCycle.pratiques || [],
        productivites: productionCycle.productivites || [],
        charges: productionCycle.charges || [],
        produits: productionCycle.produits || [],
      });
    }
  }, [productionCycle]);

  useEffect(() => {
    const fetchRelatedCycles = async () => {
      const prodId = productionCycle?.production?.id;
      if (prodId) {
        try {
          const response = await httpClient.current?.get(`/api/production-cycle`);
          if (response?.data) {
            const filtered = response.data.filter((c: any) =>
              c.production?.id === prodId && c.id.toString() !== id
            );
            setRelatedCycles(filtered);
          }
        } catch (error) {
          console.error('Erreur cycles', error);
        }
      }
    };
    fetchRelatedCycles();
  }, [productionCycle, id, httpClient]);

  const currentActivityId = formData.subActivity?.activity?.id ?? activityId;

  const filteredNatureCharges = natureCharges.filter((nc: any) => {
    if (!currentActivityId || !nc.activity) return true;
    return nc.activity.id == currentActivityId;
  });

  const filteredNatureProducts = natureProducts.filter((np: any) => {
    if (!currentActivityId || !np.activity) return true;
    return np.activity.id == currentActivityId;
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const onProductivityQuantityChange = (globalIndex: number, value: number | undefined) => {
    const updated = [...formData.productivites];
    if (updated[globalIndex]) {
      updated[globalIndex] = { ...updated[globalIndex], quantity: value };
      setFormData({ ...formData, productivites: updated });
    }
  };

  const handleTogglePractice = (practice: any) => {
    const current = formData.pratiques || [];
    const exists = current.find((p: any) => p.id === practice.id);
    if (exists) {
      setFormData({ ...formData, pratiques: current.filter((p: any) => p.id !== practice.id) });
    } else {
      setFormData({ ...formData, pratiques: [...current, { id: practice.id, name: practice.name || practice.label }] });
    }
  };

  const openFinancialModal = (type: 'CHARGE' | 'PRODUCT', item: any, index: number) => {
    setEditType(type);
    setEditItem({ ...item, index });
    setFinancialAlert({ show: false, message: '' });
    setModalFinancial(true);
  };

  const closeFinancialModal = () => {
    setModalFinancial(false);
    setFinancialAlert({ show: false, message: '' });
  };

  const handleFinancialChange = (field: string, value: any) => {
    setEditItem({ ...editItem, [field]: value });
    setFinancialAlert({ show: false, message: '' });
  };

  const saveFinancialUpdate = () => {
    const listName = editType === 'CHARGE' ? 'charges' : 'produits';
    const natureId = editType === 'CHARGE' ? editItem?.natureCharge?.id : editItem?.natureProduct?.id;

    const isDuplicateNature = formData[listName].some((item: any, index: number) => {
      if (index === editItem.index) return false;
      const itemNatureId = editType === 'CHARGE' ? item?.natureCharge?.id : item?.natureProduct?.id;
      return itemNatureId != null && itemNatureId === natureId;
    });

    if (isDuplicateNature) {
      setFinancialAlert({
        show: true,
        message: editType === 'CHARGE' ? props.t('production_cycle.duplicate_charge') : props.t('production_cycle.duplicate_product'),
      });
      return;
    }

    const newList = [...formData[listName]];
    newList[editItem.index] = editItem;
    setFormData({ ...formData, [listName]: newList });
    closeFinancialModal();
  };

  const onSubmit = async () => {
    setSpinner(true);

    if (formData.startDate > formData.endDate) {
      SweetAlert.fire({
        icon: 'error',
        title: 'Erreur',
        text: props.t('production_cycle.date_error') || 'La date de début doit être avant la date de fin',
      });
      setSpinner(false);
      return;
    }

    try {
      const response = await httpClient.current?.put(`/api/production-cycle/${id}`, formData);
      if (response?.status === 200) {
        history.push(`${process.env.PUBLIC_URL}/settings/production-cycle/${id}`);
      }
    } catch (error) {
      console.error("Erreur lors de l'update", error);
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('err') || 'Une erreur est survenue lors de la mise à jour',
      });
    } finally {
      setSpinner(false);
    }
  };

  if (loadingCycle) return <div className="text-center p-5"><Spinner color="primary" /></div>;

  return (
    <Fragment>
      <Breadcrumb
        parent={props.t('production_cycle.list')}
        url={'settings/production-cycle'}
        title={props.t('production_cycle.edit_title')}
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm">
              <CardBody>
                {/* Contexte en lecture seule : Projet / TPE / Production */}
                <div className="p-3 mb-4 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <h6 className="mb-3 text-muted">{props.t('production_cycle.relations')}</h6>
                  <Row>
                    <Col md="4">
                      <div className="form-group mb-2">
                        <label className="small text-muted mb-1">{props.t('production_cycle.project')}</label>
                        <div className="font-weight-bold">{formData.tpe?.project?.name || '-'}</div>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="form-group mb-2">
                        <label className="small text-muted mb-1">{props.t('production_cycle.tpe')}</label>
                        <div className="font-weight-bold">
                          {formData.tpe?.name || '-'}
                        </div>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="form-group mb-2">
                        <label className="small text-muted mb-1">{props.t('production_cycle.production')}</label>
                        <div className="font-weight-bold">{formData.production?.name || '-'}</div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Informations de base */}
                <div className="form-group">
                  <label>{props.t('production_cycle.label')}</label>
                  <input
                    className="form-control"
                    value={formData.label ? parseProductionCyclePath(formData.label).productionCycleLabel : ''}
                    disabled
                    style={{ backgroundColor: '#f4f4f4' }}
                  />
                </div>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>{props.t('production_cycle.start_date')} *</label>
                      <input type="date" className="form-control" name="startDate" value={formData.startDate} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>{props.t('production_cycle.end_date')} *</label>
                      <input type="date" className="form-control" name="endDate" value={formData.endDate} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>{props.t('production_cycle.mode')} *</label>
                      <select className="form-control" name="mode" value={formData.mode} onChange={handleInputChange}>
                        <option value={ProductionModes.conventionnel}>Conventionnel</option>
                        <option value={ProductionModes.agroécologie}>Agroécologie</option>
                      </select>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>{props.t('production_cycle.dimension')}</label>
                      <input type="number" className="form-control" name="dimension" value={formData.dimension} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Section Pratiques */}
                <div className="mt-4">
                  <AccordionElement title={props.t('production_cycle.practices')}>
                    <div style={{ padding: '16px 8px 8px' }}>
                      <Button color="success" outline onClick={() => setModalPractices(true)} className="mb-3">
                        + {props.t('practice.add_practice')}
                      </Button>
                      <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                        {formData.pratiques?.map((p: any) => (
                          <span
                            key={p.id}
                            className="badge badge-light border p-2 d-flex align-items-center shadow-sm"
                            style={{ color: '#2d6a4f' }}
                          >
                            {p.name || p.label}
                            <span
                              role="button"
                              onClick={() => handleTogglePractice(p)}
                              className="ml-2 text-danger"
                              style={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              &times;
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </AccordionElement>
                </div>

                {/* Section Productivité */}
                <div className="mt-4">
                  <AccordionElement title={props.t('production_cycle.productivity')}>
                    <div style={{ padding: '16px 8px 8px' }}>
                      <ProductivityTable
                        t={props.t}
                        productivites={formData.productivites || []}
                        unitProductionName={formData.production?.unitProduction?.value || ''}
                        showEditAction={true}
                        editableQuantities={true}
                        onQuantityChange={onProductivityQuantityChange}
                      />
                    </div>
                  </AccordionElement>
                </div>

                {/* Section Charges */}
                <div className="mt-4">
                  <AccordionElement title={props.t('production_cycle.charges')}>
                    <div style={{ padding: '16px 8px 8px' }}>
                      <ProductionCycleChargesTable
                        t={props.t}
                        charges={formData.charges || []}
                        showEditAction
                        onEditCharge={(charge, index) => openFinancialModal('CHARGE', charge, index)}
                      />
                    </div>
                  </AccordionElement>
                </div>

                {/* Section Produits */}
                <div className="mt-4">
                  <AccordionElement title={props.t('production_cycle.products')}>
                    <div style={{ padding: '16px 8px 8px' }}>
                      <ProductionCycleProductsTable
                        t={props.t}
                        produits={formData.produits || []}
                        showEditAction
                        onEditProduct={(product, originalIndex) =>
                          openFinancialModal('PRODUCT', product, originalIndex)
                        }
                      />
                    </div>
                  </AccordionElement>
                </div>

                {/* Boutons Actions */}
                <div className="mt-4 d-flex justify-content-end pt-3 border-top">
                  <Button color="secondary" className="mr-2" onClick={() => history.goBack()}>
                    {props.t('cancel')}
                  </Button>
                  <Button color="success" outline onClick={onSubmit} disabled={spinner}>
                    {spinner ? <Spinner size="sm" /> : props.t('production_cycle.update')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* MODALE PRATIQUES */}
      <Modal isOpen={modalPractices} toggle={() => setModalPractices(false)} centered>
        <ModalHeader toggle={() => setModalPractices(false)}>
          {props.t('production_cycle.practices')}
        </ModalHeader>
        <ModalBody>
          <InputGroup className="mb-3">
            <Input
              placeholder={props.t('practice.search_practice')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {allPractices
              .filter((p: any) => (p.name || p.label || '').toLowerCase().includes(searchTerm.toLowerCase()))
              .map((item: any) => (
                <div
                  key={item.id}
                  className="p-2 border-bottom d-flex justify-content-between"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: formData.pratiques?.some((p: any) => p.id === item.id) ? '#f0fff4' : 'transparent',
                  }}
                  onClick={() => handleTogglePractice(item)}
                >
                  <span>{item.name || item.label}</span>
                  {formData.pratiques?.some((p: any) => p.id === item.id) && (
                    <span className="text-success">✓</span>
                  )}
                </div>
              ))}
          </div>
        </ModalBody>
      </Modal>

      {/* MODALE FINANCIÈRE */}
      <Modal isOpen={modalFinancial} toggle={closeFinancialModal} centered>
        <ModalHeader toggle={closeFinancialModal}>
          {editType === 'CHARGE' ? props.t('production_cycle.edit_charge') : props.t('production_cycle.edit_product')}
        </ModalHeader>
        <ModalBody>
          <Alert isOpen={financialAlert.show} color="danger" fade={true} toggle={() => setFinancialAlert({ show: false, message: '' })}>
            {financialAlert.message}
          </Alert>
          <FormGroup>
            <Label>
              {editType === 'CHARGE' ? props.t('production_cycle.nature_charge') : props.t('production_cycle.nature_product')}
            </Label>
            <Input
              type="select"
              value={editType === 'CHARGE' ? editItem?.natureCharge?.id : editItem?.natureProduct?.id}
              onChange={(e) => {
                const idSel = parseInt(e.target.value);
                if (editType === 'CHARGE') {
                  const nature = filteredNatureCharges.find((n) => n.id === idSel);
                  handleFinancialChange('natureCharge', nature);
                } else {
                  const nature = filteredNatureProducts.find((n) => n.id === idSel);
                  handleFinancialChange('natureProduct', nature);
                }
              }}
            >
              <option value="">-- {props.t('production_cycle.select')} --</option>
              {editType === 'CHARGE'
                ? filteredNatureCharges.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)
                : filteredNatureProducts.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)
              }
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>{props.t('production_cycle.description')}</Label>
            <Input
              type="text"
              value={editItem?.description || ''}
              onChange={(e) => handleFinancialChange('description', e.target.value)}
            />
          </FormGroup>

          {editType === 'PRODUCT' && (
            <FormGroup>
              <Label>{props.t('production_cycle.quantity')}</Label>
              <Input
                type="number"
                value={editItem?.quantity ?? ''}
                onChange={(e) =>
                  handleFinancialChange('quantity', e.target.value === '' ? undefined : parseFloat(e.target.value))
                }
                onFocus={(e) => e.target.value === '0' && (e.target.value = '')}
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>{props.t('production_cycle.amount')}</Label>
            <InputGroup>
              <Input
                type="number"
                value={editItem?.amount ?? ''}
                onChange={(e) =>
                  handleFinancialChange('amount', e.target.value === '' ? undefined : parseFloat(e.target.value))
                }
                onFocus={(e) => e.target.value === '0' && (e.target.value = '')}
              />
              <div className="input-group-append">
                <span className="input-group-text">{editItem?.currency || 'MAD'}</span>
              </div>
            </InputGroup>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeFinancialModal}>{props.t('cancel')}</Button>
          <Button color="success" onClick={saveFinancialUpdate}>{props.t('save')}</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default translate(ProductionCycleEdit);